/** Review-Ansicht für netz-fabrik (F16): alle Meldungen mit Status-Filter.
 * Styling nach Design-Handoff (Screen 05). */

import { useState } from "react"
import { useQuery } from "@tanstack/react-query"
import { Link } from "react-router-dom"
import { datumKurz, PRIO_LABEL, STATUS_FARBE, STATUS_LABEL, TYP_LABEL } from "@/lib/labels"
import { api, apiError } from "@/services/api"
import { useAuthStore } from "@/store/auth"
import type { ReportStatus } from "@/types"

const FILTER: (ReportStatus | "alle")[] = [
  "alle",
  "neu",
  "gesichtet",
  "in_umsetzung",
  "erledigt",
  "abgelehnt",
  "verworfen",
]

export function ReviewPage() {
  const istReviewer = useAuthStore((s) => s.isReviewer())
  const [filter, setFilter] = useState<ReportStatus | "alle">("neu")

  const { data, isLoading, error } = useQuery({
    queryKey: ["reports", "review", filter],
    queryFn: () =>
      api.reports.list({
        scope: "org",
        ...(filter !== "alle" ? { status: filter } : {}),
      }),
    enabled: istReviewer,
  })

  // Anwendungs-Spalte nur bei mehreren Anwendungen der Instanz
  const { data: anwendungen } = useQuery({
    queryKey: ["applications"],
    queryFn: api.applications.list,
    enabled: istReviewer,
  })
  const appName = new Map((anwendungen ?? []).map((a) => [a.id, a.name]))
  const mehrereApps = (anwendungen?.length ?? 0) > 1

  if (!istReviewer) {
    return (
      <p className="rounded-md bg-destructive/10 px-4 py-3 text-destructive">
        Keine Berechtigung — die Review-Ansicht ist netz-fabrik vorbehalten.
      </p>
    )
  }

  return (
    <div>
      <h1 className="mb-1.5 text-[26px] font-bold tracking-tight">Review</h1>
      <p className="mb-5 text-sm text-muted-foreground">
        Alle Meldungen der Organisation, zur Sichtung durch netz-fabrik.
      </p>

      <div className="mb-5 flex flex-wrap gap-2">
        {FILTER.map((wert) => (
          <button
            key={wert}
            type="button"
            onClick={() => setFilter(wert)}
            className={`rounded-full px-4 py-2 text-[13px] font-semibold transition-colors ${
              filter === wert
                ? "bg-primary text-primary-foreground"
                : "border bg-card hover:border-primary/40"
            }`}
          >
            {wert === "alle" ? "Alle" : STATUS_LABEL[wert]}
          </button>
        ))}
      </div>

      {isLoading && <p className="text-muted-foreground">Lade Meldungen …</p>}
      {error != null && (
        <p className="rounded-md bg-destructive/10 px-4 py-3 text-destructive">
          {apiError(error)}
        </p>
      )}

      {data && data.total === 0 && (
        <p className="rounded-xl border bg-card px-6 py-10 text-center text-muted-foreground">
          Keine Meldungen
          {filter !== "alle" ? ` im Status „${STATUS_LABEL[filter]}“` : ""}.
        </p>
      )}

      {data && data.total > 0 && (
        <div className="overflow-hidden rounded-xl border bg-card shadow-card">
          <table className="w-full border-collapse text-sm">
            <thead>
              <tr className="border-b text-left text-xs font-bold uppercase tracking-wide text-muted-foreground/70">
                <th className="py-3 pl-5 pr-4 font-bold">#</th>
                <th className="py-3 pr-4 font-bold">Titel</th>
                {mehrereApps && <th className="py-3 pr-4 font-bold">Anwendung</th>}
                <th className="py-3 pr-4 font-bold">Typ</th>
                <th className="py-3 pr-4 font-bold">Status</th>
                <th className="py-3 pr-4 font-bold">Priorität</th>
                <th className="py-3 pr-4 font-bold">Melder</th>
                <th className="py-3 pr-4 font-bold">Issue</th>
                <th className="py-3 pr-5 font-bold">Erstellt</th>
              </tr>
            </thead>
            <tbody>
              {data.data.map((report) => (
                <tr
                  key={report.id}
                  className="border-b last:border-b-0 hover:bg-muted/50"
                >
                  <td className="py-3.5 pl-5 pr-4 text-muted-foreground/70">{report.id}</td>
                  <td className="py-3.5 pr-4 font-semibold">
                    <Link to={`/meldungen/${report.id}`} className="text-primary hover:underline">
                      {report.titel || <em className="text-muted-foreground">ohne Titel</em>}
                    </Link>
                  </td>
                  {mehrereApps && (
                    <td className="py-3.5 pr-4">
                      {appName.get(report.applicationId) ?? report.applicationId}
                    </td>
                  )}
                  <td className="py-3.5 pr-4">{report.typ ? TYP_LABEL[report.typ] : "—"}</td>
                  <td className="py-3.5 pr-4">
                    <span
                      className={`rounded-full px-2.5 py-1 text-xs font-bold ${STATUS_FARBE[report.status]}`}
                    >
                      {STATUS_LABEL[report.status]}
                    </span>
                  </td>
                  <td className="py-3.5 pr-4">{PRIO_LABEL[report.prioritaet]}</td>
                  <td className="py-3.5 pr-4">{report.melderName || report.melderSub}</td>
                  <td className="py-3.5 pr-4">
                    {report.forgejoIssueNr ? (
                      report.forgejoIssueUrl ? (
                        <a
                          href={report.forgejoIssueUrl}
                          target="_blank"
                          rel="noreferrer"
                          title="Forgejo-Issue öffnen"
                          className="font-semibold text-primary hover:underline"
                        >
                          #{report.forgejoIssueNr}
                        </a>
                      ) : (
                        <span className="font-semibold text-primary">#{report.forgejoIssueNr}</span>
                      )
                    ) : (
                      <span className="text-muted-foreground/70">—</span>
                    )}
                  </td>
                  <td className="py-3.5 pr-5 text-muted-foreground">
                    {datumKurz(report.createdAt)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  )
}
